/**
 * CreateSessionWizard Component
 *
 * Two-step wizard for creating inoculation sessions.
 * Step 1: Add sterilised bag lines (recipe + quantity), set session date and algorithm toggle.
 * Step 2: Review & adjust per-strain, per-format allocation, then create session.
 *
 * Allocation shape: each strain gets a quantity per bag format (recipe).
 * Validation: each format's column total must equal that format's bag count from Step 1.
 */

import { useState, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import ToggleSwitch from '@/components/ToggleSwitch'
import { useSpawnRecipes } from '@/hooks/useSpawnRecipes'
import { useSpawnUsageHistory } from '@/hooks/useSpawnUsageHistory'
import { useSpawnDemandSupply, useSpawnSessionMutations } from '@/hooks/useSpawnSessions'
import { useStrains } from '@/hooks/useLC'
import { Badge } from '@/components/ui/badge'
import { Loader2, ArrowLeft, AlertTriangle, Plus, Trash2, Check } from 'lucide-react'
import { format, getISOWeek } from 'date-fns'

function generateSessionCode(date) {
  const d = date instanceof Date ? date : new Date(date)
  const year = format(d, 'yyyy')
  const week = String(getISOWeek(d)).padStart(2, '0')
  return `SS-${year}-W${week}`
}

/**
 * Get strain ratios from usage history or demand data.
 * Returns [{ strainId, strainName, strainCode, ratio, context }]
 * where ratio is the proportional share (0-1) of total allocation.
 */
function getStrainRatios(useDemand, usageHistory, demandSupply, strains) {
  if (useDemand) {
    const withGap = demandSupply.filter(d => d.supply_gap > 0)
    if (!withGap.length) {
      return strains.map(s => ({
        strainId: s.id,
        strainName: s.name,
        strainCode: s.code,
        ratio: 0,
        context: {
          source: 'demand',
          bags_ready: demandSupply.find(d => d.strain_id === s.id)?.bags_ready || 0,
          bags_incubating: demandSupply.find(d => d.strain_id === s.id)?.bags_incubating || 0,
          urgency: demandSupply.find(d => d.strain_id === s.id)?.urgency || 'no_demand',
          reason: 'No supply gaps'
        }
      }))
    }

    const urgencyOrder = { overdue: 0, urgent: 1, soon: 2, planned: 3, no_demand: 4 }
    withGap.sort((a, b) => {
      const ua = urgencyOrder[a.urgency] ?? 4
      const ub = urgencyOrder[b.urgency] ?? 4
      if (ua !== ub) return ua - ub
      return b.supply_gap - a.supply_gap
    })

    const totalGap = withGap.reduce((sum, d) => sum + d.supply_gap, 0)
    return withGap.map(d => ({
      strainId: d.strain_id,
      strainName: d.strain_name,
      strainCode: d.strain_code,
      ratio: totalGap > 0 ? d.supply_gap / totalGap : 0,
      context: {
        source: 'demand',
        bags_ready: d.bags_ready || 0,
        bags_incubating: d.bags_incubating || 0,
        urgency: d.urgency,
        supply_gap: d.supply_gap
      }
    }))
  }

  // Usage history mode
  if (!usageHistory.length) {
    return strains.map(s => ({
      strainId: s.id,
      strainName: s.name,
      strainCode: s.code,
      ratio: 0,
      context: { source: 'manual', reason: 'No usage history' }
    }))
  }

  const totalUsed = usageHistory.reduce((sum, s) => sum + s.total_bags_used, 0)
  return usageHistory.map(s => ({
    strainId: s.strain_id,
    strainName: s.strain_name,
    strainCode: s.strain_code,
    ratio: totalUsed > 0 ? s.total_bags_used / totalUsed : 0,
    context: {
      source: 'usage_history',
      total_bags_used: s.total_bags_used,
      avg_weekly: s.avg_weekly,
      avg_weekly_kg: s.avg_weekly_kg || 0
    }
  }))
}

/**
 * Distribute `total` items across entries by their ratio, with remainder distribution.
 * Returns array of integers that sum to `total`.
 */
function distributeByRatio(total, ratios) {
  if (!ratios.length || total <= 0) return ratios.map(() => 0)

  const raw = ratios.map(r => total * r)
  const floored = raw.map(v => Math.floor(v))
  let remainder = total - floored.reduce((a, b) => a + b, 0)

  // Sort indices by fractional part descending
  const indices = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac)

  for (const { i } of indices) {
    if (remainder <= 0) break
    floored[i] += 1
    remainder--
  }

  return floored
}

/**
 * Build allocation grid: strains × formats.
 * Returns [{ strainId, strainName, strainCode, context, formats: { [recipeId]: quantity } }]
 */
function buildAllocationGrid(strainRatios, bagLines) {
  return strainRatios.map((strain, strainIdx) => {
    const formats = {}
    for (const line of bagLines) {
      // Distribute this format's bags across strains by ratio
      const quantities = distributeByRatio(
        line.quantity,
        strainRatios.map(s => s.ratio)
      )
      formats[line._lineId] = quantities[strainIdx]
    }
    return {
      strainId: strain.strainId,
      strainName: strain.strainName,
      strainCode: strain.strainCode,
      context: strain.context,
      formats
    }
  })
}

const URGENCY_COLORS = {
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  urgent: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  soon: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  no_demand: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
}

export default function CreateSessionWizard({ open, onOpenChange, onSessionCreated }) {
  const [step, setStep] = useState(1)

  // Step 1
  const [bagDraft, setBagDraft] = useState({ recipeId: '', quantity: '' })
  const [bagLines, setBagLines] = useState([])
  const [sessionDate, setSessionDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [useDemand, setUseDemand] = useState(false)

  // Step 2 — allocation grid: [{ strainId, ..., formats: { lineId: qty } }]
  const [allocations, setAllocations] = useState([])
  const [planLines, setPlanLines] = useState([]) // snapshot of bagLines used to generate plan
  const [saving, setSaving] = useState(false)

  // Data hooks
  const { recipes, loading: recipesLoading } = useSpawnRecipes()
  const { usageHistory, loading: usageLoading } = useSpawnUsageHistory(90)
  const { demandSupply, loading: demandLoading } = useSpawnDemandSupply()
  const { strains } = useStrains()
  const { createSession, addSessionItem } = useSpawnSessionMutations()

  const dataLoading = recipesLoading || usageLoading || demandLoading

  const totalBags = useMemo(() =>
    bagLines.reduce((sum, l) => sum + l.quantity, 0),
    [bagLines]
  )

  // Per-format column totals (what's been allocated) — uses planLines (Step 2 snapshot)
  const formatTotals = useMemo(() => {
    const totals = {}
    for (const line of planLines) {
      totals[line._lineId] = allocations.reduce(
        (sum, a) => sum + (a.formats[line._lineId] || 0), 0
      )
    }
    return totals
  }, [allocations, planLines])

  // Check if all format columns are balanced
  const allFormatsBalanced = useMemo(() =>
    planLines.every(line => formatTotals[line._lineId] === line.quantity),
    [planLines, formatTotals]
  )

  const planTotalBags = useMemo(() =>
    planLines.reduce((sum, l) => sum + l.quantity, 0),
    [planLines]
  )

  const totalAllocated = useMemo(() =>
    allocations.reduce((sum, a) =>
      sum + planLines.reduce((s, l) => s + (a.formats[l._lineId] || 0), 0), 0
    ),
    [allocations, planLines]
  )

  // --- Step 1 handlers ---

  const handleAddBagLine = useCallback(() => {
    const qty = parseInt(bagDraft.quantity, 10)
    if (!bagDraft.recipeId || !qty || qty <= 0) return

    const recipe = recipes.find(r => r.id === bagDraft.recipeId)
    if (!recipe) return

    setBagLines(prev => [...prev, {
      _lineId: crypto.randomUUID(),
      recipeId: recipe.id,
      recipeName: recipe.name,
      grainType: recipe.grain_type,
      bagWeightKg: recipe.bag_weight_kg,
      quantity: qty
    }])
    setBagDraft({ recipeId: '', quantity: '' })
  }, [bagDraft, recipes])

  const handleDeleteBagLine = useCallback((lineId) => {
    setBagLines(prev => prev.filter(l => l._lineId !== lineId))
  }, [])

  const handleBagLineQtyChange = useCallback((lineId, value) => {
    const num = parseInt(value, 10)
    setBagLines(prev => prev.map(l =>
      l._lineId === lineId ? { ...l, quantity: isNaN(num) ? 0 : Math.max(0, num) } : l
    ))
  }, [])

  // --- Step 2 ---

  const handleGeneratePlan = () => {
    if (totalBags <= 0) return

    // Snapshot the active bag lines for use in Step 2
    const activeLines = bagLines.filter(l => l.quantity > 0)
    if (!activeLines.length) return

    const strainRatios = getStrainRatios(useDemand, usageHistory, demandSupply, strains)
    const grid = buildAllocationGrid(strainRatios, activeLines)

    setPlanLines(activeLines)
    setAllocations(grid)
    setStep(2)
  }

  const handleFormatQtyChange = useCallback((strainIdx, lineId, value) => {
    const num = parseInt(value, 10)
    setAllocations(prev => {
      const next = [...prev]
      next[strainIdx] = {
        ...next[strainIdx],
        formats: {
          ...next[strainIdx].formats,
          [lineId]: isNaN(num) ? 0 : Math.max(0, num)
        }
      }
      return next
    })
  }, [])

  const handleCreateSession = async () => {
    if (!allFormatsBalanced) return

    const itemsToCreate = []
    for (const alloc of allocations) {
      for (const line of planLines) {
        const qty = alloc.formats[line._lineId] || 0
        if (qty > 0) {
          itemsToCreate.push({
            strainId: alloc.strainId,
            containerType: 'bag',
            containerSize: line.bagWeightKg,
            grainType: line.grainType,
            quantity: qty,
            safetyMarginQty: 0
          })
        }
      }
    }

    if (!itemsToCreate.length) return

    setSaving(true)
    try {
      const code = generateSessionCode(sessionDate)
      const formatSummary = planLines.map(l => `${l.quantity}x ${l.recipeName}`).join(', ')
      const session = await createSession({
        code,
        name: `Session ${code}`,
        scheduledDate: sessionDate,
        notes: `${useDemand ? 'Demand-driven' : 'Usage-history'} allocation. ${planTotalBags} bags (${formatSummary}).`
      })

      for (const item of itemsToCreate) {
        await addSessionItem(session.id, item)
      }

      onSessionCreated?.(session)
      onOpenChange(false)
      resetForm()
    } catch (err) {
      console.error('[CreateSessionWizard] Error creating session:', err)
    } finally {
      setSaving(false)
    }
  }

  const resetForm = () => {
    setStep(1)
    setBagDraft({ recipeId: '', quantity: '' })
    setBagLines([])
    setPlanLines([])
    setSessionDate(format(new Date(), 'yyyy-MM-dd'))
    setUseDemand(false)
    setAllocations([])
  }

  const handleOpenChange = (isOpen) => {
    if (!isOpen) resetForm()
    onOpenChange(isOpen)
  }

  const canGenerate = totalBags > 0 && !dataLoading
  const draftQty = parseInt(bagDraft.quantity, 10)
  const hasMultipleFormats = step === 2 ? planLines.length > 1 : bagLines.length > 1

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={hasMultipleFormats && step === 2 ? 'odin-modal-large' : 'odin-modal-medium'}>
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Create Inoculation Session' : 'Review Allocation'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-5 py-2">
            {/* Bag lines — progressive disclosure input */}
            <div className="space-y-3">
              <Label>Sterilised bags</Label>
              <div className="flex items-end gap-2">
                <div className="w-[220px]">
                  <Label className="text-xs text-muted-foreground mb-1">Recipe</Label>
                  <Select
                    value={bagDraft.recipeId}
                    onValueChange={(value) => setBagDraft(prev => ({ ...prev, recipeId: value, quantity: '' }))}
                  >
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="Select recipe" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipes.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name} ({r.grain_type}, {r.bag_weight_kg}kg)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {bagDraft.recipeId && (
                  <div className="w-[80px]">
                    <Label className="text-xs text-muted-foreground mb-1">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={bagDraft.quantity}
                      onChange={(e) => setBagDraft(prev => ({ ...prev, quantity: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddBagLine()
                        }
                      }}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                )}

                {draftQty > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 px-3"
                    onClick={handleAddBagLine}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {bagLines.length > 0 && (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Recipe</TableHead>
                        <TableHead>Grain</TableHead>
                        <TableHead className="w-[80px]">Weight</TableHead>
                        <TableHead className="w-[80px]">Qty</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bagLines.map((line) => (
                        <TableRow key={line._lineId}>
                          <TableCell className="font-medium">{line.recipeName}</TableCell>
                          <TableCell className="capitalize">{line.grainType}</TableCell>
                          <TableCell>{line.bagWeightKg}kg</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={line.quantity}
                              onChange={(e) => handleBagLineQtyChange(line._lineId, e.target.value)}
                              className="h-8 w-16 text-center"
                            />
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => handleDeleteBagLine(line._lineId)}
                              className="p-1 text-gray-500 hover:text-red-600"
                              title="Remove"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="px-4 py-2 text-sm font-medium text-content-secondary border-t">
                    Total: {totalBags} bags
                  </div>
                </div>
              )}
            </div>

            {/* Session date */}
            <div className="space-y-1.5">
              <Label htmlFor="sessionDate">Session date</Label>
              <Input
                id="sessionDate"
                type="date"
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
              />
            </div>

            {/* Algorithm toggle */}
            <div className="flex items-center gap-3">
              <ToggleSwitch
                id="algorithm-toggle"
                checked={useDemand}
                onCheckedChange={setUseDemand}
              />
              <Label htmlFor="algorithm-toggle" className="cursor-pointer text-sm">
                {useDemand ? 'Use demand forecast' : 'Use usage history'}
              </Label>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Summary */}
            <div className="text-sm text-content-secondary">
              {planTotalBags} bags across {allocations.filter(a =>
                planLines.some(l => (a.formats[l._lineId] || 0) > 0)
              ).length} strains
              <span className="ml-2 text-content-tertiary">
                ({planLines.map(l => `${l.quantity}x ${l.recipeName}`).join(', ')})
              </span>
            </div>

            {/* Allocation grid */}
            <div className="max-h-[450px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Strain</TableHead>
                    {planLines.map(line => (
                      <TableHead key={line._lineId} className="text-center w-[100px]">
                        <div className="text-xs leading-tight">
                          <div>{line.recipeName}</div>
                          <div className="text-content-tertiary font-normal">{line.bagWeightKg}kg</div>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-center w-[70px]">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.map((alloc, strainIdx) => {
                    const strainTotal = planLines.reduce(
                      (sum, l) => sum + (alloc.formats[l._lineId] || 0), 0
                    )
                    return (
                      <TableRow key={alloc.strainId}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{alloc.strainName}</span>
                            <Badge variant="outline" className="text-xs shrink-0">
                              {alloc.strainCode}
                            </Badge>
                            {alloc.context?.urgency && alloc.context.urgency !== 'no_demand' && (
                              <Badge className={`text-xs ${URGENCY_COLORS[alloc.context.urgency] || ''}`}>
                                {alloc.context.urgency}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-content-tertiary mt-0.5">
                            {alloc.context?.source === 'usage_history' && (
                              <span>{alloc.context.total_bags_used} bags used (90d), ~{alloc.context.avg_weekly}/wk ({alloc.context.avg_weekly_kg}kg/wk)</span>
                            )}
                            {alloc.context?.source === 'demand' && (
                              <span>
                                Ready: {alloc.context.bags_ready} | Incubating: {alloc.context.bags_incubating}
                                {alloc.context.supply_gap != null && ` | Gap: ${alloc.context.supply_gap}`}
                              </span>
                            )}
                            {alloc.context?.source === 'manual' && (
                              <span>{alloc.context.reason}</span>
                            )}
                          </div>
                        </TableCell>
                        {planLines.map(line => (
                          <TableCell key={line._lineId} className="text-center">
                            <Input
                              type="number"
                              min="0"
                              className="w-16 text-center mx-auto h-8"
                              value={alloc.formats[line._lineId] || 0}
                              onChange={(e) => handleFormatQtyChange(strainIdx, line._lineId, e.target.value)}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-center font-medium text-sm">
                          {strainTotal}
                        </TableCell>
                      </TableRow>
                    )
                  })}

                  {/* Column totals row */}
                  <TableRow className="border-t-2 font-medium">
                    <TableCell className="text-sm">Column totals</TableCell>
                    {planLines.map(line => {
                      const colTotal = formatTotals[line._lineId] || 0
                      const target = line.quantity
                      const isBalanced = colTotal === target
                      return (
                        <TableCell key={line._lineId} className="text-center">
                          <div className={`flex items-center justify-center gap-1 text-sm ${
                            isBalanced ? 'text-green-600' :
                            colTotal > target ? 'text-red-600' :
                            'text-amber-600'
                          }`}>
                            {isBalanced && <Check className="h-3.5 w-3.5" />}
                            {!isBalanced && <AlertTriangle className="h-3.5 w-3.5" />}
                            {colTotal} / {target}
                          </div>
                        </TableCell>
                      )
                    })}
                    <TableCell className="text-center text-sm">
                      <span className={totalAllocated === planTotalBags ? 'text-green-600' : 'text-amber-600'}>
                        {totalAllocated} / {planTotalBags}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Validation summary */}
            {!allFormatsBalanced && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Each format column must match its target before creating the session.</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="mr-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}

          {step === 1 ? (
            <Button onClick={handleGeneratePlan} disabled={!canGenerate}>
              {dataLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Generate Plan'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleCreateSession}
              disabled={saving || !allFormatsBalanced || totalAllocated === 0}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Session'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
